import { useMemo } from 'react';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { Container, DayOfWeekLabel } from './index.styles';
import { useCalendarContext } from '../../../../context';
import { startOfWeek } from '../../../../utils';

interface Props {
  showWeekNumbers?: boolean;
  userWeekStartDay: UserWeekStartDay;
}

export const Header = ({ showWeekNumbers, userWeekStartDay }: Props) => {
  const user = useBridgeUser();
  const { currentDate } = useCalendarContext();

  const locale = user?.language || 'en-US';

  const days = useMemo(() => {
    const start = startOfWeek(currentDate, userWeekStartDay);

    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(start);

      dt.setDate(start.getDate() + i);

      return dt;
    });
  }, [currentDate, userWeekStartDay]);

  const dayNumbers = useMemo(() => days.map((d) => d.getDate()), [days]);

  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });

    return days.map((d) => {
      const raw = formatter.format(d);

      return raw.charAt(0).toUpperCase() + raw.slice(1);
    });
  }, [days, locale]);

  const todayMidnight = useMemo(() => {
    const t = new Date();

    t.setHours(0, 0, 0, 0);

    return t.getTime();
  }, []);

  const isPastFlags = useMemo(
    () =>
      days.map((d) => {
        const ts = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

        return ts < todayMidnight;
      }),
    [days, todayMidnight]
  );

  return (
    <Container $useGap={!!showWeekNumbers}>
      {weekdayLabels.map((wd, i) => (
        <DayOfWeekLabel
          data-testid="day-of-week-label"
          $isToday={days[i].toDateString() === new Date().toDateString()}
          $isPast={showWeekNumbers ? isPastFlags[i] : false}
          key={wd}
          aria-label={wd}
        >
          <p>{wd}</p>
          {showWeekNumbers && (
            <span>
              {dayNumbers[i]}
              {dayNumbers[i] === 1 && <strong> {days[i].toLocaleString(locale, { month: 'short' })}</strong>}
            </span>
          )}
        </DayOfWeekLabel>
      ))}
    </Container>
  );
};
